import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export interface MentorContactVisibility {
  id: string;
  mentor_id: string;
  hide_phone_from_all: boolean;
  hide_email_from_connected: boolean;
  hide_linkedin_from_connected: boolean;
  admins_can_always_see: boolean;
  created_at: string;
  updated_at: string;
}

export function useMentorContactVisibility() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["mentor_contact_visibility", user?.id],
    queryFn: async (): Promise<MentorContactVisibility | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("mentor_contact_visibility")
        .select("*")
        .eq("mentor_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
}

export interface UpdateMentorContactVisibilityParams {
  hide_phone_from_all?: boolean;
  hide_email_from_connected?: boolean;
  hide_linkedin_from_connected?: boolean;
}

export function useUpdateMentorContactVisibility() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateMentorContactVisibilityParams) => {
      if (!user?.id) throw new Error("Not authenticated");

      // Check if record exists
      const { data: existing } = await supabase
        .from("mentor_contact_visibility")
        .select("id")
        .eq("mentor_id", user.id)
        .maybeSingle();

      if (existing) {
        // Update existing record
        const { error } = await supabase
          .from("mentor_contact_visibility")
          .update({
            ...params,
            updated_at: new Date().toISOString(),
          })
          .eq("mentor_id", user.id);

        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from("mentor_contact_visibility")
          .insert({
            mentor_id: user.id,
            hide_phone_from_all: params.hide_phone_from_all ?? false,
            hide_email_from_connected: params.hide_email_from_connected ?? false,
            hide_linkedin_from_connected: params.hide_linkedin_from_connected ?? false,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentor_contact_visibility"] });
      // Invalidate shared contact info queries so changes take effect immediately
      queryClient.invalidateQueries({ queryKey: ["shared_contact_info"] });
      toast.success("Privacy settings updated");
    },
    onError: (error: any) => {
      toast.error("Failed to update privacy settings: " + error.message);
    },
  });
}
